import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import { List } from 'immutable';

import { getLeftNavCollapsed } from 'selectors/nav';

import { MSG_DRAWER_WIDTH } from 'features/messenger/constants';
import MessengerDrawerContent from 'features/messengerv2/MessengerView/components/MessengerDrawer';
import ScrimGeneralModal from 'features/modals/ScrimGeneralModal';
import {
  LEFT_COLLAPSED_LEFT_NAV_WIDTH,
  LEFT_EXPANDED_LEFT_NAV_WIDTH,
  LEFT_NAV_DRAWER_CLIP_MASK_WIDTH,
  LEFT_NAV_DRAWER_PADDING,
  ZINDEX_LEFT_NAV_DRAWER_LAYER,
  ZINDEX_LEFTNAV_DRAWER_WRAPPERS,
} from 'features/nav/LeftNav/constants';

import Drawer from 'components/Drawer';
import Loader from 'components/Loader';
import ScrimWrapper from 'components/ScrimWrapper';

import { cxHelpers } from 'util/className';

const { cxEl } = cxHelpers('MessengerView');

interface Props {
  isShown: boolean;
  isLoading: boolean;
  shouldHideTabs: boolean;
  currentTab: string;
  handleClose: () => void;
  handleTabChange: (tab: string) => void;
  tabs: List<any>;
  repackagingEnabled: boolean;
  showMessengerScrim: boolean;
  menuOpen: boolean;
  handleMenuClose: () => void;
  toggleMenuOpen: () => void;
  isManagerial: boolean;
  handleCloseMessengerScrim: () => void;
  customZIndex?: number;
  showScrim?: boolean;
}

export const MessengerDrawer = ({
  isShown,
  isLoading,
  shouldHideTabs,
  currentTab,
  handleClose,
  handleTabChange,
  tabs,
  repackagingEnabled,
  showMessengerScrim,
  menuOpen,
  handleMenuClose,
  toggleMenuOpen,
  isManagerial,
  handleCloseMessengerScrim,
}: Props) => {
  const isCollapsed = useSelector(getLeftNavCollapsed);

  const LEFT_NAV_LEFT_POSITION = useMemo(
    () =>
      (isCollapsed
        ? LEFT_COLLAPSED_LEFT_NAV_WIDTH
        : LEFT_EXPANDED_LEFT_NAV_WIDTH) -
      LEFT_NAV_DRAWER_CLIP_MASK_WIDTH +
      LEFT_NAV_DRAWER_PADDING * 2,
    [isCollapsed]
  );

  return (
    <ScrimWrapper
      isOpen={showMessengerScrim}
      arrowFlipY
      arrowTop={50}
      arrowRight={360}
      arrowRotate={30}
      arrowType="slightlyCurved"
      closeCallback={handleCloseMessengerScrim}
      content={
        <Box mb={100} hcenter>
          <ScrimGeneralModal
            i18nPrefix="message_drawer.scrim"
            onRequestClose={handleCloseMessengerScrim}
          />
        </Box>
      }
    >
      <Box
        style={{
          position: 'fixed',
          left: isCollapsed ? 68 : 196,
          top: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          clipPath: `inset(0 0 0 ${10}px)`,
          zIndex: ZINDEX_LEFTNAV_DRAWER_WRAPPERS,
        }}
      >
        <Drawer
          zIndex={ZINDEX_LEFT_NAV_DRAWER_LAYER}
          className={cxEl('left-nav-messenger-drawer')}
          width={String(MSG_DRAWER_WIDTH)}
          open={isShown}
          containerStyle={{
            height: `calc(100vh - (2 * ${LEFT_NAV_DRAWER_PADDING}px))`,
            position: 'absolute',
            left: isCollapsed ? 20 : LEFT_NAV_LEFT_POSITION,
            top: LEFT_NAV_DRAWER_PADDING,
            bottom: LEFT_NAV_DRAWER_PADDING,
            borderRadius: '16px',
            border: '1px solid var(--mono300, #e6e4d6)',
            pointerEvents: 'auto',
            width: '440px',
            overflowX: 'hidden',
          }}
          alignLeft
        >
          <Loader
            isLoading={isLoading}
            className={cxEl(isLoading ? 'loading' : 'loaded')}
          >
            <MessengerDrawerContent
              shouldHideTabs={shouldHideTabs}
              currentTab={currentTab}
              handleClose={handleClose}
              handleTabChange={handleTabChange}
              tabs={tabs}
              repackagingEnabled={repackagingEnabled}
              showMessengerScrim={showMessengerScrim}
              menuOpen={menuOpen}
              handleMenuClose={handleMenuClose}
              toggleMenuOpen={toggleMenuOpen}
              showMenu={isManagerial}
              hideDefaultBorder
              iconSize="small"
            />
          </Loader>
        </Drawer>
      </Box>
    </ScrimWrapper>
  );
};

export default MessengerDrawer;
